import { SearchOutlined } from '@ant-design/icons';
import { Flex, Input, theme } from 'antd';
import { type Dayjs } from 'dayjs';
import React, { type ReactNode, useContext, useEffect, useState } from 'react';
import AuthorizedComponent from '../../../components/authorized-component';
import DateRangePicker from '../../../components/controls/date-range-picker';
import FilterSelect from '../../../components/controls/filter-select';
import { AuthContext } from '../../../contexts/auth-context-provider';
import useActiveCustomersWithSites from '../../../hooks/use-active-customers-with-sites';
import { useNotification } from '../../../hooks/use-notification';
import { Roles } from '../../../types';
import { LocalDate } from '../../../utils/local-date';
import { formatSiteDisplayName } from '../../../utils/utils';

export interface CustomerOrdersFiltersProps {
  onChange?: (filters: FiltersState) => void;
  initialFilters?: FiltersState;
  showSiteFilter: boolean;
}

interface FiltersState {
  query?: string;
  customer?: number[];
  site?: number[];
  orderDate?: [LocalDate, LocalDate];
}

const CustomerOrdersFilter = (props: CustomerOrdersFiltersProps): ReactNode => {
  const { initialFilters, onChange, showSiteFilter = false } = props;
  const [filters, setFilter] = useState<FiltersState>(initialFilters ?? {});
  const { token } = theme.useToken();
  const notification = useNotification();
  const { session } = useContext(AuthContext);
  // Only HHC_ADMIN filters by customer and site
  const customers = useActiveCustomersWithSites(session?.claims.role === Roles.ROLE_HHC_ADMIN);

  useEffect(() => {
    customers.errorMessage &&
      notification.error({ message: ' ', description: customers.errorMessage });
  }, [customers.errorMessage]);

  useEffect(() => {
    onChange?.(filters);
  }, [filters]);

  return (
    <Flex gap={token.paddingXS}>
      <Input
        allowClear
        value={filters?.query}
        placeholder="Search by ID"
        prefix={<SearchOutlined />}
        onChange={(e) => {
          setFilter((prev) => ({
            ...prev,
            orderDate: undefined,
            customer: undefined,
            site: undefined,
            query: e ? e.target.value.replace(/\D/g, '') || undefined : '',
          }));
        }}
        style={{ maxWidth: 300, minWidth: 200, marginInlineEnd: token.marginMD }}
      />
      <DateRangePicker
        disabled={!!filters?.query}
        value={filters.orderDate?.map((date) => date.toDayjs()) as [Dayjs, Dayjs]}
        title="Order date"
        showOnlyDateRange={true}
        onChange={(value) => {
          setFilter((prev) => ({
            ...prev,
            orderDate: (value?.map((v) => new LocalDate(v)) as [LocalDate, LocalDate]) ?? undefined,
          }));
        }}
      />
      <AuthorizedComponent roles={[Roles.ROLE_HHC_ADMIN]}>
        <FilterSelect
          disabled={!!filters?.query}
          value={filters?.customer}
          canSelectAll
          mode="multiple"
          options={customers.data}
          optionValueProp="id"
          optionLabelProp="name"
          title="Customer"
          onChange={(value) => {
            setFilter((prev) => ({
              ...prev,
              customer: value,
              site: undefined,
            }));
          }}
        />
        {showSiteFilter && filters?.customer?.length === 1 && (
          <FilterSelect
            disabled={!!filters?.query}
            value={filters?.site}
            mode="single"
            selectFirstOptionOnMount={false}
            options={(
              customers.data.find((customer) => customer.id === filters.customer?.[0])?.sites ?? []
            ).map((site) => ({
              value: site.id,
              // use formatSiteDisplayName function instead of <DetailedSite /> to fix coloring of the selected option
              label: formatSiteDisplayName(site),
            }))}
            title="Site"
            onChange={(value) => {
              setFilter((prev) => ({
                ...prev,
                site: value,
              }));
            }}
          />
        )}
      </AuthorizedComponent>
    </Flex>
  );
};
CustomerOrdersFilter.defaultProps = {
  onChange: () => {},
  initialFilters: undefined,
  showSiteFilter: false,
};
export default CustomerOrdersFilter;
